import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Plus, Car, CheckCircle2, AlertTriangle, RotateCcw, Sparkles } from "lucide-react";
import { DAMAGE_ZONES, SEVERITY, buildWhatsAppLink } from "@/lib/constants";

const fmt = (n) => Math.round(n).toLocaleString("de-DE") + " €";

/**
 * Side-view car SVG with clickable damage zones.
 * Coordinates roughly fit a 800x320 viewBox.
 */
const CarSVG = ({ selected, onZoneClick }) => {
  const zoneOn = (id) => !!selected[id];
  const fill = (id) => (zoneOn(id) ? "#1FA3B5" : "transparent");
  const stroke = (id) => (zoneOn(id) ? "#0F4651" : "rgba(15,23,42,0.18)");

  return (
    <svg viewBox="0 0 800 320" className="w-full h-auto select-none" data-testid="damage-car-svg">
      <defs>
        <linearGradient id="bodyGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F8FAFC" />
          <stop offset="100%" stopColor="#E2E8F0" />
        </linearGradient>
        <linearGradient id="windowGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#475569" />
          <stop offset="100%" stopColor="#0F172A" />
        </linearGradient>
      </defs>

      {/* Ground shadow */}
      <ellipse cx="400" cy="295" rx="320" ry="10" fill="rgba(15,23,42,0.12)" />

      {/* Car silhouette base */}
      <path
        d="M 60 220
           L 90 195
           L 130 175
           L 200 145
           L 240 120
           L 540 120
           L 600 155
           L 700 175
           L 740 200
           L 750 230
           L 730 250
           L 60 250
           Z"
        fill="url(#bodyGrad)"
        stroke="#94A3B8"
        strokeWidth="1.5"
      />

      {/* Windows */}
      <path
        d="M 220 150 L 250 128 L 415 128 L 415 155 Z M 420 128 L 535 128 L 580 158 L 420 158 Z"
        fill="url(#windowGrad)"
        opacity="0.85"
      />

      {/* Door lines */}
      <line x1="320" y1="135" x2="320" y2="248" stroke="#94A3B8" strokeWidth="1" opacity="0.6" />
      <line x1="420" y1="135" x2="420" y2="248" stroke="#94A3B8" strokeWidth="1" opacity="0.6" />
      <line x1="540" y1="135" x2="540" y2="248" stroke="#94A3B8" strokeWidth="1" opacity="0.6" />

      {/* Door handles */}
      <rect x="280" y="195" width="22" height="3" rx="1.5" fill="#94A3B8" />
      <rect x="380" y="195" width="22" height="3" rx="1.5" fill="#94A3B8" />
      <rect x="500" y="195" width="22" height="3" rx="1.5" fill="#94A3B8" />

      {/* Wheels */}
      <g onClick={() => onZoneClick("wheels")} className="cursor-pointer">
        <circle cx="180" cy="252" r="36" fill="#0F172A" />
        <circle cx="180" cy="252" r="22" fill={zoneOn("wheels") ? "#1FA3B5" : "#475569"} stroke="#94A3B8" strokeWidth="1.5" />
        <circle cx="620" cy="252" r="36" fill="#0F172A" />
        <circle cx="620" cy="252" r="22" fill={zoneOn("wheels") ? "#1FA3B5" : "#475569"} stroke="#94A3B8" strokeWidth="1.5" />
      </g>

      {/* === Clickable damage zones (overlays) === */}
      {/* Front bumper */}
      <g onClick={() => onZoneClick("front-bumper")} className="cursor-pointer transition-opacity hover:opacity-90" data-testid="zone-front-bumper">
        <path d="M 60 220 L 90 195 L 95 240 L 60 245 Z" fill={fill("front-bumper")} stroke={stroke("front-bumper")} strokeWidth="2" strokeDasharray={zoneOn("front-bumper") ? "" : "4 3"} opacity={zoneOn("front-bumper") ? 0.7 : 0.5} />
      </g>
      {/* Hood */}
      <g onClick={() => onZoneClick("hood")} className="cursor-pointer transition-opacity hover:opacity-90" data-testid="zone-hood">
        <path d="M 95 195 L 200 145 L 240 145 L 235 200 L 100 215 Z" fill={fill("hood")} stroke={stroke("hood")} strokeWidth="2" strokeDasharray={zoneOn("hood") ? "" : "4 3"} opacity={zoneOn("hood") ? 0.7 : 0.5} />
      </g>
      {/* Front fender (above front wheel) */}
      <g onClick={() => onZoneClick("fender-front")} className="cursor-pointer transition-opacity hover:opacity-90" data-testid="zone-fender-front">
        <path d="M 100 215 L 235 200 L 235 248 L 220 248 A 36 36 0 0 0 140 248 L 100 248 Z" fill={fill("fender-front")} stroke={stroke("fender-front")} strokeWidth="2" strokeDasharray={zoneOn("fender-front") ? "" : "4 3"} opacity={zoneOn("fender-front") ? 0.7 : 0.5} />
      </g>
      {/* Front door */}
      <g onClick={() => onZoneClick("door-front")} className="cursor-pointer transition-opacity hover:opacity-90" data-testid="zone-door-front">
        <rect x="240" y="135" width="80" height="113" fill={fill("door-front")} stroke={stroke("door-front")} strokeWidth="2" strokeDasharray={zoneOn("door-front") ? "" : "4 3"} opacity={zoneOn("door-front") ? 0.7 : 0.5} />
      </g>
      {/* Rear door */}
      <g onClick={() => onZoneClick("door-rear")} className="cursor-pointer transition-opacity hover:opacity-90" data-testid="zone-door-rear">
        <rect x="322" y="135" width="98" height="113" fill={fill("door-rear")} stroke={stroke("door-rear")} strokeWidth="2" strokeDasharray={zoneOn("door-rear") ? "" : "4 3"} opacity={zoneOn("door-rear") ? 0.7 : 0.5} />
      </g>
      {/* Side panel (rear quarter) */}
      <g onClick={() => onZoneClick("side-panel")} className="cursor-pointer transition-opacity hover:opacity-90" data-testid="zone-side-panel">
        <path d="M 422 135 L 538 135 L 538 248 L 422 248 Z" fill={fill("side-panel")} stroke={stroke("side-panel")} strokeWidth="2" strokeDasharray={zoneOn("side-panel") ? "" : "4 3"} opacity={zoneOn("side-panel") ? 0.7 : 0.5} />
      </g>
      {/* Rear / trunk area */}
      <g onClick={() => onZoneClick("rear")} className="cursor-pointer transition-opacity hover:opacity-90" data-testid="zone-rear">
        <path d="M 540 135 L 600 155 L 660 175 L 660 248 L 580 248 A 36 36 0 0 0 540 248 Z" fill={fill("rear")} stroke={stroke("rear")} strokeWidth="2" strokeDasharray={zoneOn("rear") ? "" : "4 3"} opacity={zoneOn("rear") ? 0.7 : 0.5} />
      </g>
      {/* Rear bumper */}
      <g onClick={() => onZoneClick("rear-bumper")} className="cursor-pointer transition-opacity hover:opacity-90" data-testid="zone-rear-bumper">
        <path d="M 660 175 L 700 175 L 740 200 L 750 230 L 730 250 L 700 250 L 660 248 Z" fill={fill("rear-bumper")} stroke={stroke("rear-bumper")} strokeWidth="2" strokeDasharray={zoneOn("rear-bumper") ? "" : "4 3"} opacity={zoneOn("rear-bumper") ? 0.7 : 0.5} />
      </g>
      {/* Roof */}
      <g onClick={() => onZoneClick("roof")} className="cursor-pointer transition-opacity hover:opacity-90" data-testid="zone-roof">
        <path d="M 220 130 L 250 122 L 535 122 L 545 130 L 215 138 Z" fill={fill("roof")} stroke={stroke("roof")} strokeWidth="2" strokeDasharray={zoneOn("roof") ? "" : "4 3"} opacity={zoneOn("roof") ? 0.7 : 0.5} />
      </g>
      {/* Mirror */}
      <g onClick={() => onZoneClick("mirror")} className="cursor-pointer transition-opacity hover:opacity-90" data-testid="zone-mirror">
        <ellipse cx="265" cy="156" rx="12" ry="6" fill={fill("mirror")} stroke={stroke("mirror")} strokeWidth="2" opacity={zoneOn("mirror") ? 0.85 : 0.5} />
      </g>

      {/* Selected markers (red dots) */}
      {Object.entries(selected).map(([id, sev]) => {
        const positions = {
          "front-bumper": { x: 78, y: 222 },
          "hood": { x: 165, y: 180 },
          "fender-front": { x: 165, y: 230 },
          "door-front": { x: 280, y: 195 },
          "door-rear": { x: 370, y: 195 },
          "side-panel": { x: 480, y: 195 },
          "rear": { x: 600, y: 200 },
          "rear-bumper": { x: 720, y: 220 },
          "roof": { x: 380, y: 130 },
          "mirror": { x: 265, y: 156 },
          "wheels": { x: 400, y: 280 },
          "interior": { x: 380, y: 165 },
        };
        const p = positions[id];
        if (!p) return null;
        return (
          <g key={id}>
            <circle cx={p.x} cy={p.y} r="10" fill={SEVERITY[sev]?.color || "#EF4444"} stroke="white" strokeWidth="2" />
            <circle cx={p.x} cy={p.y} r="10" fill="none" stroke={SEVERITY[sev]?.color || "#EF4444"} strokeWidth="2" opacity="0.4">
              <animate attributeName="r" from="10" to="18" dur="1.5s" repeatCount="indefinite" />
              <animate attributeName="opacity" from="0.4" to="0" dur="1.5s" repeatCount="indefinite" />
            </circle>
          </g>
        );
      })}
    </svg>
  );
};

const DamageConfigurator = () => {
  // selected = { zoneId: severityKey }
  const [selected, setSelected] = useState({});

  const totals = useMemo(() => {
    let dealer = 0;
    Object.entries(selected).forEach(([id, sev]) => {
      const zone = DAMAGE_ZONES.find((z) => z.id === id);
      const mult = SEVERITY[sev]?.multiplier ?? 1;
      if (zone) dealer += zone.basePrice * mult;
    });
    return { dealer, save: dealer };
  }, [selected]);

  const addZone = (id) => {
    setSelected((s) => ({ ...s, [id]: s[id] || "leicht" }));
  };
  const removeZone = (id) => {
    setSelected((s) => {
      const cp = { ...s };
      delete cp[id];
      return cp;
    });
  };
  const setSeverity = (id, sev) => {
    setSelected((s) => ({ ...s, [id]: sev }));
  };
  const reset = () => setSelected({});

  const selectedList = Object.entries(selected).map(([id, sev]) => ({
    zone: DAMAGE_ZONES.find((z) => z.id === id),
    sev,
  })).filter(x => x.zone);

  const waMessage = `Hallo AutoCoach, ich habe den Schadens-Konfigurator genutzt.

Meine markierten Schäden:
${selectedList.map((s) => `• ${s.zone.label} (${SEVERITY[s.sev].label})`).join("\n") || "(keine ausgewählt)"}

Geschätzte Nachzahlung beim Leasinggeber: ${fmt(totals.dealer)}
Mit AutoCoach: 0 € Nachzahlung
Ersparnis: ${fmt(totals.save)}

Bitte melden Sie sich für ein konkretes Festpreis-Angebot.`;

  return (
    <section id="konfigurator" data-testid="damage-configurator" className="relative py-20 sm:py-24 lg:py-32 bg-white overflow-hidden">
      <div className="absolute -top-32 right-0 w-[500px] h-[500px] bg-[#E6F6F8] rounded-full blur-3xl opacity-60 pointer-events-none" />
      <div className="absolute -bottom-32 left-0 w-[500px] h-[500px] bg-[#1FA3B5]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1FA3B5] text-white text-xs font-semibold mb-5 shadow-lg shadow-[#1FA3B5]/30">
            <Car className="w-3.5 h-3.5" /> Schadens-Konfigurator
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-slate-900">
            Markieren Sie Ihre Schäden<br />
            <span className="text-[#1FA3B5]">direkt am Fahrzeug.</span>
          </h2>
          <p className="mt-4 sm:mt-5 text-sm sm:text-base lg:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Klicken Sie auf die betroffenen Stellen — wir berechnen sofort, was der Leasinggeber dafür verlangen würde.
          </p>
        </motion.div>

        <div className="mt-12 grid lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Car SVG */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-slate-50 to-white rounded-3xl border border-slate-200 p-5 sm:p-8 shadow-xl shadow-slate-900/5"
            >
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">
                Klicken Sie auf die Schadens-Bereiche
              </p>
              <CarSVG selected={selected} onZoneClick={addZone} />

              {/* Extra zones not on the SVG: Innenraum */}
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => addZone("interior")}
                  data-testid="zone-btn-interior"
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 text-sm font-semibold transition-all ${
                    selected["interior"]
                      ? "bg-[#1FA3B5] text-white border-[#1FA3B5]"
                      : "bg-white text-slate-700 border-slate-200 hover:border-[#1FA3B5]"
                  }`}
                >
                  <Plus className="w-3.5 h-3.5" /> Innenraum
                </button>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="zone-reset"
                  className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Auswahl löschen
                </button>
              </div>
            </motion.div>
          </div>

          {/* Side panel — selection + price */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="bg-slate-900 text-white rounded-3xl p-5 sm:p-7 shadow-2xl shadow-slate-900/30 border border-slate-800 lg:sticky lg:top-32">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-[#5DD8E5]" />
                <h3 className="font-heading text-lg font-bold">Ihre Schäden</h3>
                <span className="ml-auto text-xs text-slate-400">
                  {selectedList.length} markiert
                </span>
              </div>

              {/* List */}
              <div className="min-h-[120px] max-h-[280px] overflow-y-auto -mx-1 px-1">
                <AnimatePresence>
                  {selectedList.length === 0 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex flex-col items-center justify-center text-center py-8 text-slate-400"
                      data-testid="config-empty"
                    >
                      <AlertTriangle className="w-8 h-8 text-slate-600 mb-2" />
                      <p className="text-sm">Noch keine Schäden markiert.</p>
                      <p className="text-xs mt-1 text-slate-500">Klicken Sie links auf das Fahrzeug.</p>
                    </motion.div>
                  )}
                  {selectedList.map(({ zone, sev }) => (
                    <motion.div
                      key={zone.id}
                      layout
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.25 }}
                      className="bg-slate-800/60 rounded-xl p-3 mb-2 border border-slate-700"
                      data-testid={`config-item-${zone.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold">{zone.label}</span>
                        <button
                          type="button"
                          onClick={() => removeZone(zone.id)}
                          className="text-slate-400 hover:text-white"
                          data-testid={`config-remove-${zone.id}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-1 mt-2">
                        {Object.entries(SEVERITY).map(([key, s]) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setSeverity(zone.id, key)}
                            data-testid={`config-sev-${zone.id}-${key}`}
                            className={`flex-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md transition-all ${
                              sev === key
                                ? "text-white shadow-md"
                                : "bg-slate-700 text-slate-400 hover:bg-slate-600"
                            }`}
                            style={sev === key ? { backgroundColor: s.color } : {}}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {/* Price */}
              <div className="mt-5 pt-5 border-t border-slate-700">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Beim Leasinggeber</span>
                  <span className="font-bold line-through decoration-red-400 decoration-2 text-slate-300" data-testid="config-dealer">
                    {fmt(totals.dealer)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-slate-400">Mit AutoCoach</span>
                  <span className="font-bold text-[#5DD8E5]">0 €</span>
                </div>
                <div className="mt-4 bg-gradient-to-br from-[#1FA3B5] to-[#0F4651] rounded-xl p-4 text-center">
                  <p className="text-[11px] uppercase tracking-wider font-bold text-[#5DD8E5]">Ihre Ersparnis</p>
                  <p className="font-heading text-3xl sm:text-4xl font-extrabold mt-1" data-testid="config-savings">
                    {fmt(totals.save)}
                  </p>
                </div>
              </div>

              <a
                href={buildWhatsAppLink(waMessage)}
                target="_blank"
                rel="noreferrer"
                data-testid="config-whatsapp"
                className={`mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold transition-all ${
                  selectedList.length > 0
                    ? "bg-[#25D366] hover:bg-[#1DAE54] text-white shadow-xl shadow-[#25D366]/30 hover:-translate-y-0.5"
                    : "bg-slate-700 text-slate-400 cursor-not-allowed"
                }`}
                onClick={(e) => { if (selectedList.length === 0) e.preventDefault(); }}
              >
                <MessageCircle className="w-5 h-5" />
                Festpreis per WhatsApp anfragen
              </a>
              <p className="text-[10px] text-slate-500 text-center mt-3">
                Werte sind branchenübliche Schätzungen. Verbindliches Angebot nach Foto-Check.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DamageConfigurator;
